import { useId, type MutableRefObject, type RefCallback, type TextareaHTMLAttributes } from "react";
import { styled } from "@pigment-css/react";
import { useAutoGrowTextArea } from "@acto/taskpane/field-sales-2/utils/hooks/useAutoGrowTextArea";

const MAX_LINES = 5;

// The field is capped in whole lines, so the line box has to be a pixel number; a unitless
// line-height would land the cap part-way through a line.
const LINE_HEIGHT_PX = 24;

const Frame = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[10],
  width: "100%",
  paddingRight: theme.fieldSalesTwo.spacing[12],
  boxSizing: "border-box",
}));

const Label = styled("label")(({ theme }) => ({
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size["14"],
  fontWeight: theme.fieldSalesTwo.font.weight["500"],
  lineHeight: theme.fieldSalesTwo.font.lineHeight["168"],
  color: theme.fieldSalesTwo.colors.accent.action,
}));

const TextArea = styled("textarea")(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  margin: 0,
  padding: 0,
  border: "none",
  outline: "none",
  resize: "none",
  background: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.input,
  fontWeight: theme.fieldSalesTwo.font.weight["400"],
  lineHeight: `${LINE_HEIGHT_PX}px`,
  color: theme.fieldSalesTwo.colors.neutral[900],
  overflowY: "auto",
}));

interface LabeledTextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "rows"> {
  label: string;
  value: string;
  ref?: RefCallback<HTMLTextAreaElement> | MutableRefObject<HTMLTextAreaElement | null>;
  /** Called with the field's height in pixels each time it is fitted to its text. */
  onHeightChange?: (heightPx: number) => void;
}

/** Grows with its text up to `MAX_LINES` lines, then scrolls. */
export function LabeledTextArea({ label, id, value, ref, onHeightChange, ...textAreaProps }: LabeledTextAreaProps) {
  const fallbackId = useId();
  const fieldId = id ?? fallbackId;
  const textAreaRef = useAutoGrowTextArea(value, { lineHeightPx: LINE_HEIGHT_PX, maxLines: MAX_LINES, onHeightChange });
  const setTextArea = (element: HTMLTextAreaElement | null) => {
    textAreaRef.current = element;
    if (typeof ref === "function") ref(element);
    else if (ref) ref.current = element;
  };

  return (
    <Frame>
      <Label htmlFor={fieldId}>{label}</Label>
      <TextArea {...textAreaProps} id={fieldId} ref={setTextArea} rows={1} value={value} />
    </Frame>
  );
}
